import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { ArrowUpRight, CircleCheck, ClipboardClock, Settings, SquareText, type LucideIcon } from "lucide-react";
import { useAuth } from "../auth/OAuthContext";
import { apiClient } from "../api/client";
import { OrderFlowShell } from "../components/OrderFlowShell";
import { useDocumentRealtimeInvalidation } from "../hooks/useDocumentRealtime";
import { DocumentRow } from "../types";

export function DoctorQueue() {
  const { idToken } = useAuth();
  const [documents, setDocuments] = useState<DocumentRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async (opts?: { showLoading?: boolean }) => {
    if (!idToken) return;
    if (opts?.showLoading !== false) setLoading(true);
    try {
      const response = await apiClient(idToken).getQueue("all");
      setDocuments(response.documents);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [idToken]);

  useEffect(() => { void refresh(); }, [refresh]);
  useDocumentRealtimeInvalidation(idToken, () => refresh({ showLoading: false }));

  const metrics = useMemo(() => ({
    total: documents.length,
    pending: documents.filter((doc) => doc.status === "pending").length,
    fixRequired: documents.filter((doc) => doc.status === "fix_requested").length,
    approved: documents.filter((doc) => doc.status === "signed").length,
  }), [documents]);

  const pendingDocs = useMemo(() => documents
    .filter((doc) => doc.status === "pending")
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, 6), [documents]);

  return (
    <OrderFlowShell
      title="Dashboard"
      subtitle="Review all documents routed to your physician organization."
      breadcrumb="/ Dashboard"
    >
      {error && (
        <div className="mb-6 p-4 rounded-2xl bg-[#F9EEEE] border border-[#D83933]/20 text-[#D83933] text-sm font-semibold" role="alert">
          {error}
        </div>
      )}

      {/* 4 Figma Worklist Metric Cards (Figma 35:245 / 35:274) */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5 mb-6" aria-label="Worklist statistics">
        <MetricCard label="All documents" value={metrics.total} icon={SquareText} />
        <MetricCard label="Pending" value={metrics.pending} icon={ClipboardClock} />
        <MetricCard label="Fix required" value={metrics.fixRequired} icon={Settings} />
        <MetricCard label="Approved" value={metrics.approved} icon={CircleCheck} />
      </section>

      {/* Chapter Cards Section (Figma 35:245 / 35:304 - template EL-a7d75363) */}
      <section className="flex flex-col gap-4" aria-label="Pending documents">
        {loading ? (
          <div className="py-20 text-center text-[#7F8A92] text-sm font-medium bg-[#FDFFFC] rounded-2xl border border-[#E6E8EA]">
            Loading documents...
          </div>
        ) : pendingDocs.length === 0 ? (
          <div className="py-20 text-center text-[#7F8A92] text-sm font-medium bg-[#FDFFFC] rounded-2xl border border-dashed border-[#E6E8EA]">
            No documents are pending review.
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {pendingDocs.map((doc) => (
              <div
                key={doc.id}
                className="w-full p-4 rounded-2xl bg-[#FDFFFC] border border-[#E6E8EA] shadow-[0px_8px_18px_-6px_rgba(24,39,75,0.12)] flex flex-col sm:flex-row sm:items-center justify-between gap-4"
              >
                <div className="flex items-center gap-4 min-w-0">
                  {/* Icon container (Figma 35:306 - EL-0bcee893: rounded-full, bg #F2F4FA) */}
                  <div className="w-14 h-14 rounded-full bg-[#F2F4FA] text-[#012699] flex items-center justify-center shrink-0">
                    <ClipboardClock size={24} aria-hidden="true" />
                  </div>
                  {/* Middle group (Figma 35:309 - EL-2d7cdc13) */}
                  <div className="flex flex-col gap-1 min-w-0">
                    <span className="text-sm font-bold text-[#0B2981] font-sans">
                      {formatDashboardDate(doc.created_at)}
                    </span>
                    <strong className="text-xl font-bold text-[#2D2E2F] font-sans truncate">
                      {doc.patient_ref || "Untitled Patient"}
                    </strong>
                    <p className="text-sm text-[#7F8A92] font-sans">
                      Sent by <strong className="font-bold text-[#2D2E2F]">{doc.nurse_name || doc.nurse_email || "Unknown nurse"}</strong>
                      {doc.nurse_npi && ` (NPI ${doc.nurse_npi})`}
                    </p>
                  </div>
                </div>

                {/* Right button (Figma 35:316 - EL-877c1ac6: solid blue pill #012699) */}
                <div className="shrink-0 flex items-center justify-end">
                  <Link
                    to={`/documents/${doc.id}`}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-sans font-bold text-base text-[#FDFFFC] bg-[#012699] hover:bg-[#0B2981] transition-colors"
                  >
                    <ArrowUpRight size={20} aria-hidden="true" />
                    <span>Review</span>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </OrderFlowShell>
  );
}

function MetricCard({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: number;
  icon: LucideIcon;
}) {
  return (
    <div className="p-4 rounded-2xl bg-[#FDFFFC] border border-[#E6E8EA] shadow-[0px_8px_18px_-6px_rgba(24,39,75,0.12)] flex flex-col justify-between gap-2.5">
      <div className="flex items-center justify-between">
        <span className="text-base font-bold text-[#000000] font-sans">{label}</span>
        <div className="w-14 h-14 rounded-full bg-[#F2F4FA] text-[#012699] flex items-center justify-center shrink-0">
          <Icon size={24} aria-hidden="true" />
        </div>
      </div>
      <div className="text-[32px] font-semibold text-[#000000] font-display leading-none">{value}</div>
    </div>
  );
}

function formatDashboardDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const calendar = date.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
  const weekday = date.toLocaleDateString("en-US", { weekday: "long" });
  const time = date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
  return `${calendar}, ${weekday}, ${time}`;
}
